import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ComponentClass, ComponentProps, Dispatch, ForwardedRef, forwardRef, Ref, SetStateAction, useContext, useEffect, useState } from "react";
import { ContextProp, TableProps, TableVirtuoso, TableVirtuosoHandle } from "react-virtuoso";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { AxiosError } from "axios";
import CustomTextField from "./CustomTextField";
import { JoyfulStoreSet } from "../types";
import type { CompanyListInfo, RegisterCompanyArgs } from "../types";
import { checkHalfKanaOnly } from "../utils/common";

const commonTableCellStyles = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 500,
  fontSize: 12,
  letterSpacing: "0.01071em",
  display: "table-cell",
  verticalAlign: "inherit",
  borderBottom: "1px solid rgba(224, 224, 224, 1)",
  textAlign: "left",
  padding: "16px",
  paddingTop: "20px",
  paddingBottom: "20px",
  color: "rgba(0, 0, 0, 0.87)",
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
  maxWidth: "110px",
  borderLeft: "1px solid rgb(224, 224, 224)",
  borderTop: "1px solid rgb(224, 224, 224)",
};
const headStyles = {
  ...commonTableCellStyles,
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "#fbefb4",
};
const textStyle = {
  textAlign: "right",
  marginRight: "16px",
};

type CompanyEditProps = {
  companies: CompanyListInfo[];
  stores: JoyfulStoreSet[];
  open: boolean;
  isUpdating: boolean;
  onClose: () => void;
  companyName: string;
  companyCd: string;
  centerCd: string;
  onUpdate: (params: RegisterCompanyArgs) => Promise<void>;
  setCenterCd: (cd: string) => void;
  setCommitStores: Dispatch<SetStateAction<JoyfulStoreSet[]>>;
};

const virtuosoHeader = () => (
  // <TableHead component="thead">
    <TableRow>
      <TableCell sx={headStyles} align="center">
        店舗名
      </TableCell>
      <TableCell sx={headStyles} align="center">
        店舗コード（先方）
      </TableCell>
      <TableCell sx={headStyles} align="center">
        店舗名（先方）
      </TableCell>
    </TableRow>
  // </TableHead>
);

const virtuosoBody = (
  idx: number,
  row: Partial<JoyfulStoreSet>,
  updateRow: (index: number, data: Partial<JoyfulStoreSet>) => void
) => (
  <>
    <TableCell>{row.client_store_temp}</TableCell>
    <TableCell sx={{ ...commonTableCellStyles, maxWidth: 150 }}>
      <CustomTextField
        id={`storeCd-${idx}`}
        type="text"
        label="例）0001"
        text={row.client_store_cd ?? ''}
        setText={(newValue) => {
          updateRow(idx, { client_store_cd: newValue });
        }}
        maxLength={50}
        size="small"
        isNumericText
      />
    </TableCell>
    <TableCell sx={{ ...commonTableCellStyles, maxWidth: 150 }}>
      <CustomTextField
        id={`storeName-${idx}`}
        type="text"
        label="例）ｺﾊﾞｯｸ（半角ｶﾅ）"
        text={row.client_store_name ?? ''}
        setText={(newValue) => {
          updateRow(idx, { client_store_name: newValue });
        }}
        maxLength={100}
        size="small"
        allowHalfKanaOnly
      />
    </TableCell>
  </>
)

const virtuosoTable = forwardRef<HTMLTableElement, ComponentProps<typeof Table>>((props, ref) => <Table {...props} ref={ref} />)
const virtuosoTableBody = forwardRef((props: any, ref: ForwardedRef<HTMLTableSectionElement>) => (
  <TableBody component="tbody" ref={ref} {...props} />
))

/**
 * CompanyEditModal
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
export default function CompanyEditModal({
  companies,
  stores,
  open,
  isUpdating,
  onClose,
  companyName,
  companyCd,
  centerCd,
  onUpdate,
  setCenterCd,
  setCommitStores,
}: CompanyEditProps) {
  const [localStores, setLocalStores] = useState<JoyfulStoreSet[]>([]);
  const { showAlert } = useContext(SuccessAlertContext);

  useEffect(() => {
    if (open) {
      setLocalStores(stores);
      // setLocalStores(stores.slice(0,1000));
    }
  }, [open, JSON.stringify(stores)]);

  // 先方の店舗情報更新
  const updateRow = (index: number, data: Partial<JoyfulStoreSet>) => {
    setLocalStores((prev) => {
      const copy = structuredClone(prev);
      copy[index] = { ...copy[index], ...data };
      return copy;
    });
    // setCommitStores((prev) => {
    //   const copy = [...prev];
    //   copy[index] = { ...copy[index], ...data };
    //   return copy;
    // });
  };
  const handleCompanyUpdate = async () => {
    if (!localStores.every(lStore => checkHalfKanaOnly(lStore.client_store_name))) {
      showAlert("店舗名は全て半角ｶﾅで入力してください。", "error");
      return;
    }
    try {
      await onUpdate({
        companyCd,
        centerCd,
        stores: localStores.filter(s => !!s.client_cd),
      });
      onClose();
    } catch (err) {
      console.error(err);
      const error2 = err as AxiosError;
      // レスポンス自体がなくエラーになった時
      if (!error2.response) {
        Notify.setNotification({
          show: true,
          title: "APIに接続できませんでした。",
          confirmLabel: "OK",
          actionsPosition: "end",
        });
        return;
      }
      const { status } = error2.response;
      const data = error2.response.data as {code: string, message: string};
      switch (status) {
        // case 400:
        //   Notify.setNotification({
        //     show: true,
        //     title: "パラメータエラーです。",
        //     confirmLabel: "OK",
        //     actionsPosition: "end",
        //   });
        //   break;
        case 422:
          Notify.setNotification({
            // show: true,
            // title: data.messages.join('\n'),
            // confirmLabel: "OK",
            // actionsPosition: "end",
            show: true,
            title: "入力エラーがあります。",
            message: data.message,
            titleColor: "var(--error)",
          });
          break;
        default:
          break;
      }
    }
  };
  // const handleClose = () => {
  //   onClose();
  // };

  // const tableHeadElement = () => (
  //   <TableRow>
  //     <TableCell sx={headStyles} align="center">
  //       店舗名
  //     </TableCell>
  //     <TableCell sx={headStyles} align="center">
  //       店舗コード（先方）
  //     </TableCell>
  //     <TableCell sx={headStyles} align="center">
  //       店舗名（先方）
  //     </TableCell>
  //   </TableRow>
  // );
  // const tableBodyElement = () => (
  //   <TableBody>
  //     {localStores.map((row, idx) => (
  //       <TableRow>
  //         <TableCell>{row.client_store_name}</TableCell>
  //         <TableCell sx={{ ...commonTableCellStyles, maxWidth: 150 }}>
  //           <CustomTextField
  //             id={`storeCd-${idx}`}
  //             type="text"
  //             label="例）0001"
  //             text={row.client_store_cd}
  //             setText={(newValue) => {
  //               updateRow(idx, { client_store_cd: newValue });
  //             }}
  //             maxLength={12}
  //             size="small"
  //             isNumericText
  //           />
  //         </TableCell>
  //         <TableCell sx={{ ...commonTableCellStyles, maxWidth: 150 }}>
  //           <CustomTextField
  //             id={`storeName-${idx}`}
  //             type="text"
  //             label="例）コバック名古屋店"
  //             text={row.client_store_name}
  //             setText={(newValue) => {
  //               updateRow(idx, { client_store_name: newValue });
  //             }}
  //             maxLength={25}
  //             size="small"
  //             // isHalfWidthKatakanaText
  //           />
  //         </TableCell>
  //       </TableRow>
  //     ))}
  //   </TableBody>
  // );

  const virtuosoStoreTable = () => (
    <Paper sx={{height: 500, width: '100%'}}>
      <TableVirtuoso
        data={localStores}
        fixedHeaderContent={() => virtuosoHeader()}
        itemContent={(idx, row) => virtuosoBody(idx, row, updateRow)}
        components={{
          Table: virtuosoTable,
          TableBody: virtuosoTableBody
        }}
      />
    </Paper>
  )

  return (
    <Dialog open={open} onClose={onClose}>
      <Box p={2}>
        <DialogTitle>企業編集</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          {isUpdating && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
              }}
            >
              <CircularProgress size={80} />
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              key={companyCd}
              sx={{
                display: "grid",
                gridTemplateColumns: "190px 200px",
                gap: 2,
                alignItems: "center",
              }}
              margin={2}
            >
              <Typography sx={textStyle}>企業名：</Typography>
              <CustomTextField
                id="companyName"
                label={companyName}
                type="text"
                text={companyName}
                setText={() => {}}
                maxLength={100}
                disabled
                fullWidth
              />

              <Typography sx={textStyle}>企業コード：</Typography>
              <CustomTextField
                id="companyCd"
                label={companyCd}
                type="text"
                text={companyCd}
                setText={() => {}}
                maxLength={6}
                disabled
                fullWidth
                isNumericText
              />

              <Typography sx={textStyle}>センターコード：</Typography>
              <CustomTextField
                id="centerCd"
                type="text"
                label="センターコード"
                text={centerCd}
                setText={setCenterCd}
                maxLength={50}
                size="small"
              />
            </Box>
          </Box>

          {/* 先方の店舗情報 */}
          <Box
            mt={3}
            display="flex"
            flexDirection="row"
            borderBottom={2}
            borderColor="primary.main"
            justifyContent="flex-start"
          >
            <Typography>先方の店舗情報</Typography>
          </Box>
          <Box mt={2}>
            <TableContainer component={Table} sx={{ overflow: "visible", width: "100%" }}>
              <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="mock_table">
                {/* <TableHead>{tableHeadElement()}</TableHead>
                {tableBodyElement()} */}
                {virtuosoStoreTable()}
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="custom-outlined" onClick={() => onClose()}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleCompanyUpdate();
            }}
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : undefined}
          >
            更新
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
